/**
 * Calendar Service
 * Handles API operations for calendar and holidays
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { APP_SCHEMA_ENDPOINTS, buildUrlWithParams } from '../api';
import {
  Holiday,
  HolidayQueryParams,
  HolidayByDateParams,
  CalendarEntry,
} from '../models/calendar.types';

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  private readonly http = inject(HttpClient);

  // Signals for reactive state management
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  /**
   * Get holidays for specific locations and date range
   * @param params Query parameters for filtering holidays
   * @returns Observable of holidays with location and school details
   */
  getHolidays(params?: HolidayQueryParams): Observable<Holiday[]> {
    this.loading.set(true);
    this.error.set(null);

    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.calendar.holidays, params);

    return this.http
      .get<Holiday[]>(url, {
        headers: this.createHeaders(),
      })
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Check if a specific date is a holiday for a given location
   * @param params Date and optional location ID
   * @returns Observable of holiday entry or null if not a holiday
   */
  getHolidayByDate(params: HolidayByDateParams): Observable<Holiday | null> {
    this.loading.set(true);
    this.error.set(null);

    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.calendar.holidayByDate, params);

    return this.http
      .get<Holiday | null>(url, {
        headers: this.createHeaders(),
      })
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Get all calendar entries (basic CRUD)
   * @param skip Pagination skip
   * @param limit Pagination limit
   * @returns Observable of calendar entries
   */
  getCalendarEntries(skip: number = 0, limit: number = 100): Observable<CalendarEntry[]> {
    this.loading.set(true);
    this.error.set(null);

    const url = buildUrlWithParams(APP_SCHEMA_ENDPOINTS.calendar.list, { skip, limit });

    return this.http
      .get<CalendarEntry[]>(url, {
        headers: this.createHeaders(),
      })
      .pipe(
        tap(() => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Create authorization headers
   */
  private createHeaders(): HttpHeaders {
    const token = this.getAuthToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    });
  }

  /**
   * Get auth token from localStorage
   */
  private getAuthToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('auth_token');
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: any): Observable<never> {
    console.error('Calendar service error:', error);

    let errorMessage = 'Ein Fehler ist aufgetreten';

    if (error.error?.detail) {
      errorMessage = error.error.detail;
    } else if (error.message) {
      errorMessage = error.message;
    }

    this.error.set(errorMessage);
    this.loading.set(false);

    return throwError(() => new Error(errorMessage));
  }
}
